import React from 'react';
import {
    CareTeamMemberDetails,
    UpsertCareTeamMember,
} from '../../../primary-record-types/apiTypes/medicalProfileService';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { InsuranceFormValue, ProviderFormValue, SupportFormValue } from './types';
import CareTeamTypeConditionalRenderContainer from './CareTeamTypeConditionalRenderContainer';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { Form } from '../../v2BaseComponents/Form';
import { useCareTeamFormHelpers } from './useCareTeamFormHelpers';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../v2BaseComponents/Form/SideDrawerFormContainer';
import { CareTeamMemberSource } from '../../../utils/careTeamUtils';

export type CareTeamSideDrawerFormOwnProps = {
    careTeamMemberDetails?: CareTeamMemberDetails;
    onSubmit: (createBody: UpsertCareTeamMember) => void;
    isSubmitting: boolean;
    source: CareTeamMemberSource;
    careTeamMemberId?: string;
    shouldRecordAddCareTeamMember?: boolean;
    onCancel: () => void;
};

export type CareTeamSideDrawerFormProps = CareTeamSideDrawerFormOwnProps;

const CareTeamSideDrawerButtonForm: React.FC<CareTeamSideDrawerFormProps> = ({
    careTeamMemberDetails,
    onSubmit,
    isSubmitting,
    source,
    careTeamMemberId,
    shouldRecordAddCareTeamMember,
    onCancel,
}) => {
    const recordEvent = useRecordEvent();
    const { getInitialValues, getSubmitValues, renderFields } = useCareTeamFormHelpers({
        initialCareTeamMemberDetails: careTeamMemberDetails || null,
        isDisabled: isSubmitting,
    });

    return (
        <AnimateLoadedContent isLoading={isSubmitting}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <CareTeamTypeConditionalRenderContainer
                        initialCareTeamMemberDetails={careTeamMemberDetails || null}
                        isDisabled={isSubmitting}
                        areUpdatesDisabled={!!careTeamMemberDetails}
                    >
                        {(careTeamMemberType) => (
                            <Form
                                fillContainer
                                initialValues={getInitialValues(careTeamMemberType)}
                                isDisabled={isSubmitting}
                                forceStackFields
                                onSubmit={(values) => {
                                    onSubmit(
                                        getSubmitValues(
                                            careTeamMemberType,
                                            values as InsuranceFormValue | ProviderFormValue | SupportFormValue,
                                        ),
                                    );
                                    if (shouldRecordAddCareTeamMember) {
                                        recordEvent('addCareTeamMember');
                                    }
                                }}
                            >
                                {({ values, handleSubmit }) => (
                                    <SideDrawerFormContainer
                                        isDisabled={isSubmitting}
                                        onSubmit={handleSubmit}
                                        onCancel={onCancel}
                                    >
                                        <SideDrawerDirtyFormExitValidationTracker />
                                        {renderFields({
                                            values: values as InsuranceFormValue | ProviderFormValue | SupportFormValue,
                                            isDisabled: isSubmitting,
                                            careTeamMemberType,
                                            source,
                                            careTeamMemberId,
                                        })}
                                    </SideDrawerFormContainer>
                                )}
                            </Form>
                        )}
                    </CareTeamTypeConditionalRenderContainer>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CareTeamSideDrawerButtonForm);
